import React from 'react'
import { Typography } from '@mui/material'
import {
  Confirm,
  Form,
  Loading,
  useNotify,
  usePermissions,
  useTranslate
} from 'react-admin'
import { includes, isNil, map, replace, split } from 'lodash/fp'
import log from 'loglevel'

import { doCognitoUpload, doUpload } from './functions'
import { type Partner } from '@mzc-watchover/adminapp-appsync'

log.setLevel(log.levels.DEBUG)

export const UploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doUpload(file, partner, includes('noStop')(permissions))
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  if (isLoadingPermissions) return <Loading />
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={map((item: string) => (
          <Typography variant="body1" key={item}>
            {item}
            <br />
          </Typography>
        ))(split('\n')(translate('resources.upload.dialog.content')))}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.upload.dialog.buttons.cancel"
        confirm="resources.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
export const CognitoUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
}> = ({ open, setOpen }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.cognito.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doCognitoUpload(file)
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={map((item: string) => (
          <Typography variant="body1" key={item}>
            {item}
            <br />
          </Typography>
        ))(split('\n')(translate('resources.cognito.upload.dialog.content')))}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.cognito.upload.dialog.buttons.cancel"
        confirm="resources.cognito.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
